/**
 * JAISIU-4442 — seekable audio playback controls.
 *
 * ## Why this exists
 *
 * Audio attachments are rendered as a native `<audio controls>`
 * element in two places:
 *
 *   - the attachment preview overlay (`attachment-preview-overlay.ts`
 *     → `renderAudioBody`);
 *   - the chat transcript (`grouped-render.ts` → `renderMessageAudios`
 *     and the inline sent-attachment player).
 *
 * The browser's native control chrome is the only seek affordance, and
 * it has the same problems the video overlay had (JAISIU-4437):
 *
 *   - in the transcript the native chrome is a fixed 28px strip that
 *     cannot show a readable elapsed / total time next to a voice note;
 *   - the native scrubber auto-hides on idle and is unreachable for
 *     keyboard users once surrounding chrome takes focus;
 *   - the native strip gives no width to a full-width progress track in
 *     the overlay, where the audio body sits in a wide card.
 *
 * So the audio player gets its OWN control bar: an always-visible
 * `role="slider"` track that is full-width, keyboard reachable, and
 * that seeks on click AND on drag, plus explicit play / pause and
 * volume controls.
 *
 * This module deliberately MIRRORS the video progress architecture
 * (JAISIU-4437, `video-progress-bar.ts`): the same pure seek
 * arithmetic, the same "read the media state LIVE at gesture time"
 * rule, the same imperative updater (a Lit re-render on every
 * `timeupdate` would tear down and recreate the `<audio>` element and
 * restart playback).
 *
 * ## Seeking semantics
 *
 * The bar is driven entirely by the `<audio>` element's own state:
 *
 *   - `audio.duration`   — the total length of the track.
 *   - `audio.currentTime` — the playhead position.
 *
 * A pointer position on the track maps to a time via the track's
 * bounding rect:
 *
 *     ratio  = (clientX - rect.left) / rect.width      (clamped 0..1)
 *     target = ratio * duration
 *
 * Clicking seeks immediately. Dragging seeks continuously while the
 * pointer is down (pointer capture, so the drag survives the pointer
 * leaving the track).
 *
 * ## Interaction contract
 *
 *   - `pointerdown` on the track → seek + begin drag.
 *   - `pointermove` while dragging → seek.
 *   - `pointerup` / `pointercancel` → end drag.
 *   - `keydown` on the track → ArrowLeft / ArrowRight nudge by
 *     `step` seconds, Home / End jump to the ends, PageUp / PageDown
 *     nudge by a larger step.
 *   - `click` on the play/pause button → toggle playback.
 *   - `input` on the volume slider → set `media.volume`.
 *
 * ## Not in scope
 *
 * This module owns the player UI only. Whether the bytes for a
 * requested range are actually delivered is a gateway concern (HTTP
 * `Range` / `206 Partial Content`) and is tracked separately. The bar
 * still reflects `audio.currentTime`, so if the gateway cannot serve
 * the range the browser clamps the seek itself and the bar stays
 * consistent with what is actually playing.
 */

import { html, type TemplateResult } from "lit";

/**
 * Smallest / largest fraction of the track a single arrow-key nudge
 * moves. Expressed in seconds so the behaviour is independent of the
 * track length.
 */
export const AUDIO_PROGRESS_STEP_SECONDS = 5;

/** Larger nudge used by PageUp / PageDown. */
export const AUDIO_PROGRESS_LARGE_STEP_SECONDS = 30;

/**
 * Clamp a value into `[min, max]`. Non-finite input collapses to
 * `min` — a NaN duration must never leak into `currentTime`, because
 * assigning NaN to `currentTime` throws in the browser.
 */
export function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Map a pointer position to a playhead ratio in `[0, 1]`.
 *
 * `rect` is the track's `DOMRect` (or any `{ left, width }` shape).
 * A zero / negative width (element not laid out yet) yields `0`
 * rather than `NaN`.
 */
export function pointerRatio(clientX: number, rect: { left: number; width: number }): number {
  if (!rect || !Number.isFinite(rect.width) || rect.width <= 0) return 0;
  if (!Number.isFinite(clientX)) return 0;
  return clampNumber((clientX - rect.left) / rect.width, 0, 1);
}

/**
 * Convert a pointer position on the track to the absolute time the
 * playhead should move to.
 *
 * Returns `0` for a track whose duration is not yet known
 * (`duration` is `NaN` before `loadedmetadata`, `Infinity` for a
 * live stream) — seeking an unknown-length track to an arbitrary
 * offset is meaningless.
 */
export function seekTimeFromPointer(
  clientX: number,
  rect: { left: number; width: number },
  duration: number,
): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return pointerRatio(clientX, rect) * duration;
}

/**
 * Fraction of the track already played, in `[0, 1]`. `0` when the
 * duration is unknown.
 */
export function playedRatio(currentTime: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isFinite(currentTime) || currentTime <= 0) return 0;
  return clampNumber(currentTime / duration, 0, 1);
}

/**
 * Format a number of seconds as `m:ss` (or `h:mm:ss` past an hour).
 * Used for the elapsed / total labels beside the track.
 */
export function formatMediaTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

/**
 * The subset of `HTMLMediaElement` the bar needs. Declared locally so
 * the module can be unit-tested with a plain object and does not force
 * a DOM lib on consumers.
 */
export interface AudioSeekTarget {
  duration: number;
  currentTime: number;
  volume?: number;
  muted?: boolean;
  paused?: boolean;
}

/**
 * A snapshot of the media state the bar renders.
 */
export interface AudioProgressState {
  /** Total track length in seconds; `0` when unknown. */
  duration: number;
  /** Playhead position in seconds. */
  currentTime: number;
}

/**
 * Read the media state off an `<audio>` (or `<video>`) element,
 * normalising the `NaN` / `Infinity` values the browser reports
 * before metadata has loaded.
 */
export function readAudioProgressState(
  media: AudioSeekTarget | null | undefined,
): AudioProgressState {
  if (!media) return { duration: 0, currentTime: 0 };
  const duration = Number.isFinite(media.duration) ? media.duration : 0;
  const currentTime = Number.isFinite(media.currentTime) ? media.currentTime : 0;
  return { duration, currentTime };
}

/**
 * Seek the media element, defensively.
 *
 * Assigning `currentTime` on a media element whose metadata has not
 * loaded yet throws in some browsers (`InvalidStateError`), so we
 * guard both the duration check and the assignment.
 */
export function applySeek(
  media: (AudioSeekTarget & { currentTime: number }) | null | undefined,
  seconds: number,
): boolean {
  if (!media) return false;
  if (!Number.isFinite(seconds) || seconds < 0) return false;
  try {
    media.currentTime = seconds;
    return true;
  } catch {
    // Media element not ready yet (no metadata) — the caller will
    // retry on the next interaction.
    return false;
  }
}

/**
 * Compute the next playhead position for a keyboard nudge.
 *
 * `key` is the `KeyboardEvent.key`. Returns `null` when the key is
 * not a seek key so the caller can let the event bubble.
 */
export function keyboardSeekTarget(
  key: string,
  state: AudioProgressState,
  step = AUDIO_PROGRESS_STEP_SECONDS,
  largeStep = AUDIO_PROGRESS_LARGE_STEP_SECONDS,
): number | null {
  const duration = state.duration;
  const current = state.currentTime;
  // Without a known duration we cannot express "5 seconds from here"
  // as a fraction of the track, and the clamp bounds are unknown, so
  // every positional nudge is a no-op. `Home` is still meaningful
  // (seek to the very start); `End` is not (we do not know where the
  // end is).
  const durationKnown = Number.isFinite(duration) && duration > 0;
  switch (key) {
    case "ArrowLeft":
    case "ArrowDown":
      if (!durationKnown) return null;
      return clampNumber(current - step, 0, duration);
    case "ArrowRight":
    case "ArrowUp":
      if (!durationKnown) return null;
      return clampNumber(current + step, 0, duration);
    case "PageDown":
      if (!durationKnown) return null;
      return clampNumber(current - largeStep, 0, duration);
    case "PageUp":
      if (!durationKnown) return null;
      return clampNumber(current + largeStep, 0, duration);
    case "Home":
      return 0;
    case "End":
      return durationKnown ? duration : null;
    default:
      return null;
  }
}

/**
 * The volume the player starts at when the media element does not
 * report a usable one. `1` is the browser default.
 */
export const AUDIO_DEFAULT_VOLUME = 1;

/**
 * Normalise a media element's volume into `[0, 1]`.
 *
 * The browser reports `NaN` before the element is configured and
 * clamps the assignment range to `[0, 1]`, so an out-of-range or
 * non-finite value collapses to the default rather than throwing.
 */
export function normalizeVolume(volume: number | undefined): number {
  if (volume === undefined || !Number.isFinite(volume)) {
    return AUDIO_DEFAULT_VOLUME;
  }
  return clampNumber(volume, 0, 1);
}

/**
 * Set the media element's volume, defensively.
 *
 * Returns the volume that was actually applied (the browser clamps
 * to `[0, 1]`).
 */
export function applyVolume(media: { volume?: number } | null | undefined, volume: number): number {
  if (!media) return AUDIO_DEFAULT_VOLUME;
  const next = normalizeVolume(volume);
  try {
    media.volume = next;
  } catch {
    return normalizeVolume(media.volume);
  }
  return normalizeVolume(media.volume);
}

/**
 * Toggle playback on a media element.
 *
 * Returns the resulting paused state (`true` when the media ended up
 * paused). A media element that cannot play (no source yet, decode
 * error) is left paused rather than throwing at the caller.
 */
export function togglePlayback(media: HTMLMediaElement | null | undefined): boolean {
  if (!media) return true;
  try {
    if (media.paused) {
      const played = media.play?.();
      // `play()` returns a promise that rejects when the media cannot
      // start; swallow it so the click handler never throws.
      if (played && typeof played.catch === "function") {
        played.catch(() => undefined);
      }
      return false;
    }
    media.pause?.();
    return true;
  } catch {
    return media.paused !== false;
  }
}

/**
 * Class name of the progress-bar track. The imperative updater and
 * the seek wiring both find the track through this class.
 */
export const AUDIO_PROGRESS_TRACK_CLASS = "attachment-preview-audio-progress-track";

/**
 * Class name of the element that holds the whole bar (track + time
 * labels + controls). The updater locates it relative to the
 * `<audio>` element.
 */
export const AUDIO_PROGRESS_ROOT_CLASS = "attachment-preview-audio-progress";

/**
 * Seek handler invoked by the progress bar.
 *
 * The handler receives the absolute target time AND the track element
 * that produced the gesture. Passing the track lets the caller resolve
 * the media element relative to the bar, which keeps the wiring local
 * — no global `document.querySelector`, no cross-talk between players.
 */
export type AudioSeekHandler = (seconds: number, track: HTMLElement) => void;

/**
 * Resolver that returns the media element the bar drives.
 *
 * The bar reads the media element's LIVE `duration` / `currentTime`
 * on every gesture rather than a render-time snapshot. This matters:
 * the player template is rendered long before the browser has loaded
 * the track's metadata, so a captured snapshot would always read
 * `duration === 0` and every seek would land at the start.
 *
 * The resolver receives the TRACK element that produced the gesture,
 * so the caller can locate the media element relative to the bar
 * instead of reaching into the whole document.
 */
export type AudioMediaResolver = (track: HTMLElement) => AudioSeekTarget | null | undefined;

/**
 * Seek helper used by the pointer handlers: maps the pointer's
 * position on the track to a time and hands it to `onSeek`.
 */
function seekAudioToPointer(
  track: HTMLElement,
  clientX: number,
  duration: number,
  onSeek: AudioSeekHandler,
): number {
  const rect = track.getBoundingClientRect();
  const target = seekTimeFromPointer(clientX, rect, duration);
  onSeek(target, track);
  return target;
}

/**
 * Render the audio playback controls.
 *
 * The bar is presentational: it wires the pointer / keyboard gestures
 * and reads the live media state. The actual seeking is delegated to
 * `onSeek`, which the caller binds to the `<audio>` element.
 *
 * The rendered parts carry stable classes (not just `data-testid`s)
 * because the media-state updater ({@link updateAudioProgressBar})
 * mutates them directly on every `timeupdate` — re-rendering the
 * whole player on each tick would restart the `<audio>` element.
 *
 * @param getMedia  Resolves the media element (live state source).
 * @param onSeek    Called with the absolute target time (seconds) on
 *                  click, drag, and keyboard nudge.
 * @param testid    Optional `data-testid` override for the root.
 */
export function renderAudioProgressBar(
  getMedia: AudioMediaResolver,
  onSeek: AudioSeekHandler,
  testid = "attachment-preview-audio-progress",
): TemplateResult {
  // The initial paint uses whatever the media element reports right
  // now (usually "duration unknown"); the imperative updater keeps it
  // in step from then on.
  const initial = readAudioProgressState(getMedia(document.body));
  const ratio = playedRatio(initial.currentTime, initial.duration);
  const durationLabel = initial.duration > 0 ? formatMediaTime(initial.duration) : "--:--";
  const currentLabel = formatMediaTime(initial.currentTime);
  const volume = normalizeVolume(getMedia(document.body)?.volume);

  return html`
    <div
      class="${AUDIO_PROGRESS_ROOT_CLASS} attachment-viewer-audio-progress"
      data-testid=${testid}
    >
      <div
        class="${AUDIO_PROGRESS_TRACK_CLASS} attachment-viewer-audio-progress-track"
        role="slider"
        tabindex="0"
        aria-label="Audio position"
        data-testid="${testid}-track"
        aria-valuemin="0"
        aria-valuemax=${initial.duration > 0 ? initial.duration : 0}
        aria-valuenow=${initial.currentTime}
        aria-valuetext=${`${currentLabel} of ${durationLabel}`}
        data-duration=${initial.duration}
        data-current-time=${initial.currentTime}
        data-ratio=${ratio}
        @pointerdown=${(e: PointerEvent) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          const track = e.currentTarget as HTMLElement;
          // Seek immediately on press so a plain click lands. The
          // duration is read LIVE — the track's metadata may have
          // loaded since the template was rendered.
          const live = readAudioProgressState(getMedia(track));
          seekAudioToPointer(track, e.clientX, live.duration, onSeek);
          try {
            track.setPointerCapture(e.pointerId);
          } catch {
            // Pointer capture is best-effort; the drag still works
            // through the element-level listeners below.
          }
          const onMove = (ev: PointerEvent) => {
            const live = readAudioProgressState(getMedia(track));
            seekAudioToPointer(track, ev.clientX, live.duration, onSeek);
          };
          const onUp = () => {
            try {
              track.releasePointerCapture(e.pointerId);
            } catch {
              // Already released.
            }
            track.removeEventListener("pointermove", onMove as EventListener);
            track.removeEventListener("pointerup", onUp as EventListener);
            track.removeEventListener("pointercancel", onUp as EventListener);
          };
          track.addEventListener("pointermove", onMove as EventListener);
          track.addEventListener("pointerup", onUp as EventListener);
          track.addEventListener("pointercancel", onUp as EventListener);
        }}
        @keydown=${(e: KeyboardEvent) => {
          const live = readAudioProgressState(getMedia(e.currentTarget as HTMLElement));
          const target = keyboardSeekTarget(e.key, live);
          if (target === null) return;
          e.preventDefault();
          e.stopPropagation();
          onSeek(target, e.currentTarget as HTMLElement);
        }}
      >
        <div
          class="attachment-preview-audio-progress-fill attachment-viewer-audio-progress-fill"
          style="width:${(ratio * 100).toFixed(2)}%;"
          data-testid="${testid}-fill"
        ></div>
      </div>
      <div
        class="attachment-preview-audio-progress-times attachment-viewer-audio-progress-times"
        data-testid="${testid}-times"
      >
        <span
          class="attachment-preview-audio-progress-current attachment-viewer-audio-progress-current"
          data-testid="${testid}-current"
          >${currentLabel}</span
        >
        <span
          class="attachment-preview-audio-progress-duration attachment-viewer-audio-progress-duration"
          data-testid="${testid}-duration"
          >${durationLabel}</span
        >
      </div>
      <div
        class="attachment-preview-audio-progress-controls attachment-viewer-audio-progress-controls"
        data-testid="${testid}-controls"
      >
        <button
          type="button"
          class="attachment-preview-audio-progress-play attachment-viewer-audio-progress-play"
          data-testid="${testid}-play"
          aria-label="Play"
          title="Play"
          @click=${(e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            const btn = e.currentTarget as HTMLElement;
            const media = getMedia(btn) as HTMLMediaElement | null | undefined;
            if (!media) return;
            togglePlayback(media);
            updateAudioProgressBar(media);
          }}
        >
          ▶
        </button>
        <label
          class="attachment-preview-audio-progress-volume-label attachment-viewer-audio-progress-volume-label"
          data-testid="${testid}-volume-label"
        >
          <span class="attachment-preview-audio-progress-volume-icon" aria-hidden="true">🔊</span>
          <input
            type="range"
            class="attachment-preview-audio-progress-volume attachment-viewer-audio-progress-volume"
            min="0"
            max="1"
            step="0.01"
            value=${volume}
            aria-label="Volume"
            data-testid="${testid}-volume"
            @input=${(e: Event) => {
              e.stopPropagation();
              const input = e.currentTarget as HTMLInputElement;
              const media = getMedia(input) as HTMLAudioElement | null | undefined;
              if (!media) return;
              const applied = applyVolume(media, Number(input.value));
              input.value = String(applied);
              updateAudioProgressBar(media);
            }}
          />
        </label>
      </div>
    </div>
  `;
}

/**
 * Update the rendered progress bar to match the media element's
 * current state.
 *
 * The bar lives next to the `<audio>` element inside the same
 * wrapper, so the updater finds it relative to the media element.
 * Returns `true` when the bar was found and updated.
 *
 * This is deliberately imperative rather than a Lit re-render: the
 * `<audio>` element's state changes several times per second, and
 * re-rendering the template on every `timeupdate` would tear down and
 * recreate the media element (which restarts playback and loses the
 * buffered range).
 */
export function updateAudioProgressBar(
  media: AudioSeekTarget | null | undefined,
  overrideCurrentTime?: number | null,
): boolean {
  if (!media) return false;
  const mediaEl = media as unknown as HTMLElement;
  if (typeof mediaEl.closest !== "function") return false;
  // The bar is a SIBLING of the media element (both live inside the
  // media wrapper), so we walk up to the wrapper and then down into
  // the bar. Looking for the bar as an ancestor of the media element
  // would never match.
  const scope = (mediaEl.closest(
    ".attachment-preview-media-wrap, .chat-message-audio-scope, .chat-message-audios",
  ) ?? mediaEl.parentElement) as HTMLElement | null;
  const host = (scope?.querySelector(`.${AUDIO_PROGRESS_ROOT_CLASS}`) ??
    null) as HTMLElement | null;
  if (!host) return false;
  const track = host.querySelector(`.${AUDIO_PROGRESS_TRACK_CLASS}`) as HTMLElement | null;
  if (!track) return false;

  const state = readAudioProgressState(media);
  // A pending seek wins over the media's (possibly stale) position —
  // the native control strip is gone (JAISIU-4447), so the custom bar
  // is the only thing that can hold the operator's requested
  // position until the browser lands the seek.
  const hasOverride =
    typeof overrideCurrentTime === "number" &&
    Number.isFinite(overrideCurrentTime);
  const displayedTime = hasOverride
    ? clampNumber(overrideCurrentTime as number, 0, Math.max(state.duration, 0))
    : state.currentTime;
  const ratio = playedRatio(displayedTime, state.duration);
  const currentLabel = formatMediaTime(displayedTime);
  const durationLabel = state.duration > 0 ? formatMediaTime(state.duration) : "--:--";

  const fill = host.querySelector(".attachment-preview-audio-progress-fill") as HTMLElement | null;
  if (fill) fill.style.width = `${(ratio * 100).toFixed(2)}%`;

  const currentEl = host.querySelector(
    ".attachment-preview-audio-progress-current",
  ) as HTMLElement | null;
  if (currentEl) currentEl.textContent = currentLabel;

  const durationEl = host.querySelector(
    ".attachment-preview-audio-progress-duration",
  ) as HTMLElement | null;
  if (durationEl) durationEl.textContent = durationLabel;

  const playBtn = host.querySelector(
    ".attachment-preview-audio-progress-play",
  ) as HTMLElement | null;
  if (playBtn) {
    const paused = media.paused !== false;
    playBtn.textContent = paused ? "▶" : "❚❚";
    playBtn.setAttribute("aria-label", paused ? "Play" : "Pause");
    playBtn.setAttribute("title", paused ? "Play" : "Pause");
    playBtn.setAttribute("data-paused", paused ? "1" : "0");
  }

  const volumeEl = host.querySelector(
    ".attachment-preview-audio-progress-volume",
  ) as HTMLInputElement | null;
  if (volumeEl && typeof volumeEl.value !== "undefined") {
    const next = normalizeVolume(media.volume);
    // Only write back when it actually changed — overwriting the
    // slider value while the operator drags it would fight the drag.
    if (Number(volumeEl.value) !== next) {
      volumeEl.value = String(next);
    }
  }

  track.setAttribute("aria-valuemax", String(state.duration));
  track.setAttribute("aria-valuenow", String(displayedTime));
  track.setAttribute("aria-valuetext", `${currentLabel} of ${durationLabel}`);
  track.setAttribute("data-duration", String(state.duration));
  track.setAttribute("data-current-time", String(displayedTime));
  track.setAttribute("data-ratio", String(ratio));
  return true;
}
